import React, { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { Bell, Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudSnow, CloudLightning, Wallet, Calendar as CalIcon, Compass, FileText } from "lucide-react";

const CURRENCY_SYM = { EUR: "€", GBP: "£" };

const WEATHER_ICONS = {
  "sun": Sun, "cloud-sun": CloudSun, "cloud": Cloud,
  "cloud-rain": CloudRain, "cloud-drizzle": CloudDrizzle,
  "cloud-snow": CloudSnow, "cloud-lightning": CloudLightning,
};

function WeatherChip({ weather, iconSize = 16 }) {
  if (!weather?.available) {
    return (
      <span className="flex items-center gap-1.5"><Sun size={iconSize} strokeWidth={1.5} /> Fair skies</span>
    );
  }
  const Icon = WEATHER_ICONS[weather.current.icon] || Sun;
  const temp = weather.current.temp_c != null ? `${Math.round(weather.current.temp_c)}°` : "";
  return (
    <span className="flex items-center gap-1.5" data-testid="weather-chip">
      <Icon size={iconSize} strokeWidth={1.5} /> {temp} {weather.current.label}
    </span>
  );
}

function OnThisDayCard({ tripId }) {
  const { API, authHeaders, token } = useAuth();
  const [otd, setOtd] = useState(null);
  useEffect(() => {
    axios.get(`${API}/trips/${tripId}/on-this-day`, { headers: authHeaders })
      .then((r) => setOtd(r.data)).catch(() => {});
  }, [tripId]);
  if (!otd?.available) return null;
  return (
    <div data-testid="on-this-day" className="mb-5 border-l-2 border-[color:var(--tcm-gold)] pl-4 py-2 flex gap-3 items-start">
      {otd.photo_id && (
        <img src={`${API}/diary/photo/${otd.photo_id}?auth=${token}`} alt=""
             className="w-16 h-16 object-cover border border-[color:var(--tcm-navy-15)] flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="tcm-eyebrow" style={{ color: "var(--tcm-gold-700)" }}>One year ago · {otd.trip_destination}</p>
        {otd.entry_title && <p className="font-display italic text-lg mt-0.5 truncate">{otd.entry_title}</p>}
        <p className="text-[13px] text-[color:var(--tcm-navy-80)] mt-0.5 line-clamp-2">{otd.entry_body}</p>
      </div>
    </div>
  );
}

function WeatherNudge({ nudge }) {
  if (!nudge) return null;
  return (
    <div data-testid="weather-nudge" className="flex gap-3 items-start p-3 border border-[color:var(--tcm-gold)]">
      <Cloud size={16} strokeWidth={1.5} style={{ color: "var(--tcm-gold-700)" }} className="mt-0.5 shrink-0" />
      <div>
        <div className="tcm-eyebrow" style={{ color: "var(--tcm-gold-700)" }}>Heads up</div>
        <div className="text-[14px] leading-[1.45] mt-0.5">{nudge.title}</div>
        <div className="text-[12px] text-[color:var(--tcm-navy-70)] mt-0.5">{nudge.body}</div>
      </div>
    </div>
  );
}

function TileCard({ to, icon, title, hint, testid }) {
  return (
    <Link to={to} data-testid={testid} className="tcm-card p-4 min-h-[104px] flex flex-col gap-1.5 hover:opacity-90 transition-opacity">
      <span style={{ color: "var(--tcm-gold-700)" }}>{icon}</span>
      <span className="font-display text-[19px] mt-auto">{title}</span>
      <span className="text-[12px] text-[color:var(--tcm-navy-70)]">{hint}</span>
    </Link>
  );
}

function MagicTouchCard({ itinerary, day }) {
  const magic = itinerary.find((i) => i.day === day && i.magic_touch);
  if (!magic) return null;
  return (
    <div className="tcm-frame-inset px-5 py-6 text-center">
      <div className="tcm-eyebrow" style={{ color: "var(--tcm-gold-700)" }}>The Magic Touch</div>
      <div className="w-9 h-px bg-[color:var(--tcm-gold)] mx-auto my-3" />
      <p className="m-0 font-display italic text-[19px] leading-[1.4]">
        {magic.hidden ? "A little surprise awaits — reveals on the day." : (magic.description || magic.title)}
      </p>
    </div>
  );
}

function dayInfo(trip) {
  try {
    const start = new Date(trip.start_date + "T00:00:00");
    const end = new Date(trip.end_date + "T00:00:00");
    const today = new Date(new Date().toDateString());
    const total = Math.round((end - start) / 86400000) + 1;
    const day = Math.round((today - start) / 86400000) + 1;
    return { day: Math.max(1, Math.min(day, total)), total, started: today >= start, ended: today > end };
  } catch { return { day: 1, total: 1, started: false, ended: false }; }
}

function timeStatus(item, now) {
  if (!item.time) return "";
  const [h, m] = item.time.split(":").map(Number);
  const itemTime = new Date(); itemTime.setHours(h, m, 0, 0);
  const end = new Date(itemTime.getTime() + 90 * 60000);
  if (now >= itemTime && now < end) return "now";
  if (now < itemTime) return "next-or-later";
  return "past";
}

export default function TodayPage() {
  const { tripId } = useParams();
  const { API, authHeaders, user } = useAuth();
  const { trip, updates } = useContext(TripCtx);
  const [itinerary, setItinerary] = useState([]);
  const [budget, setBudget] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    axios.get(`${API}/trips/${tripId}/itinerary`, { headers: authHeaders }).then((r) => setItinerary(r.data));
    axios.get(`${API}/trips/${tripId}/budget`, { headers: authHeaders }).then((r) => setBudget(r.data)).catch(() => {});
    axios.get(`${API}/trips/${tripId}/reservations`, { headers: authHeaders }).then((r) => setReservations(r.data)).catch(() => {});
    axios.get(`${API}/trips/${tripId}/weather`, { headers: authHeaders }).then((r) => setWeather(r.data)).catch(() => {});
  }, [tripId]);

  const latestUpdate = updates?.[0];
  const info = dayInfo(trip);
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const now = today;
  const todaysItems = itinerary.filter((i) => i.day === info.day);
  let nextIdx = -1;
  todaysItems.forEach((it, idx) => {
    if (nextIdx === -1 && it.time && timeStatus(it, now) !== "past") nextIdx = idx;
  });
  const nextTcmFee = budget.find((b) => b.type === "tcm_fee" && b.status === "due");
  const resCount = reservations.length;

  return (
    <div data-testid="today-page" className="px-6 pt-3 pb-10 space-y-6">
      <OnThisDayCard tripId={tripId} />

      <div>
        <p className="tcm-eyebrow">{dateStr}</p>
        <h1 className="font-display text-[38px] leading-[1.02] mt-1 font-normal">
          Good {today.getHours() < 12 ? "morning" : today.getHours() < 18 ? "afternoon" : "evening"}, {user?.full_name?.split(" ")[0]}
        </h1>
      </div>

      <div className="tcm-frame-inset px-5 py-5 grid grid-cols-[auto_1fr] gap-5 items-center">
        <div className="text-center pr-5 border-r border-[color:var(--tcm-navy-15)]">
          <div className="font-display text-[54px] leading-[0.9] tabular-nums">{info.day}</div>
          <div className="text-[11px] tracking-[0.14em] uppercase mt-1">of {info.total}</div>
        </div>
        <div>
          <div className="font-display text-[22px] leading-[1.1]">{trip.destination}</div>
          <div className="flex items-center gap-1.5 text-[13px] mt-1.5 text-[color:var(--tcm-navy-80)]">
            <WeatherChip weather={weather} />
          </div>
        </div>
      </div>

      {latestUpdate && (
        <div className="flex gap-3 items-start p-3 border border-[color:var(--tcm-gold)]">
          <Bell size={16} strokeWidth={1.5} style={{ color: "var(--tcm-gold-700)" }} className="mt-0.5 shrink-0" />
          <div>
            <div className="text-[14px] leading-[1.45]">{latestUpdate.title}</div>
            <div className="text-[12px] text-[color:var(--tcm-navy-70)] mt-0.5">{latestUpdate.body}</div>
          </div>
        </div>
      )}

      <WeatherNudge nudge={weather?.nudge} />

      <section>
        <div className="flex justify-between items-baseline pb-2 border-b border-[color:var(--tcm-gold)]">
          <h2 className="text-[24px] font-medium m-0 font-display">Today</h2>
          <Link to={`/trips/${tripId}/itinerary`} className="text-[13px] underline underline-offset-[3px]">Full day</Link>
        </div>
        {todaysItems.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-4">Nothing scheduled. The day is your own.</p>}
        {todaysItems.map((it, idx) => {
          const s = timeStatus(it, now);
          const tag = idx === nextIdx && s === "now" ? "Now" : idx === nextIdx ? "Next" : null;
          return (
            <div key={it.id} className={`grid grid-cols-[52px_1fr_auto] gap-3 py-[13px] border-b border-[color:var(--tcm-navy-15)] ${s === "past" ? "opacity-50" : ""}`}>
              <div className="font-display text-[18px] tabular-nums leading-[1.2]">{it.time || "—"}</div>
              <div>
                <div className="text-[14.5px] font-medium leading-[1.35]">{it.title}</div>
                {it.location && <div className="text-[12.5px] text-[color:var(--tcm-navy-70)] mt-0.5">{it.location}</div>}
              </div>
              {tag && <span className="tcm-tag-accent self-start">{tag}</span>}
            </div>
          );
        })}
      </section>

      <div className="grid grid-cols-2 gap-2.5">
        <TileCard to={`/trips/${tripId}/budget`} icon={<Wallet size={18} strokeWidth={1.5} />} title="Budget"
                  hint={nextTcmFee ? `${CURRENCY_SYM[nextTcmFee.currency]}${nextTcmFee.amount.toFixed(0)} fee due` : "All settled"} testid="tile-budget" />
        <TileCard to={`/trips/${tripId}/more`} icon={<CalIcon size={18} strokeWidth={1.5} />} title="Reservations"
                  hint={`${resCount} booking${resCount === 1 ? "" : "s"}`} testid="tile-reservations" />
        <TileCard to={`/trips/${tripId}/more`} icon={<Compass size={18} strokeWidth={1.5} />} title="Logistics"
                  hint="Transfers & access" testid="tile-logistics" />
        <TileCard to={`/trips/${tripId}/documents`} icon={<FileText size={18} strokeWidth={1.5} />} title="Documents"
                  hint="Visas & vouchers" testid="tile-documents" />
      </div>

      <MagicTouchCard itinerary={itinerary} day={info.day} />
    </div>
  );
}
